import "./Contact.css";
import React, { useRef, useState } from "react";
import emailjs from "@emailjs/browser";
import contactBg from "../../images/ServicePhotos/bg_1.webp";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMAILJS_SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID;
const EMAILJS_TEMPLATE_ID = process.env.REACT_APP_EMAILJS_TEMPLATE_ID;
const EMAILJS_PUBLIC_KEY = process.env.REACT_APP_EMAILJS_PUBLIC_KEY;

const isEmailJsConfigured =
  EMAILJS_SERVICE_ID && EMAILJS_TEMPLATE_ID && EMAILJS_PUBLIC_KEY;

function getFormValues(form) {
  const data = new FormData(form);
  return {
    name: data.get("name")?.toString() ?? "",
    email: data.get("email")?.toString() ?? "",
    subject: data.get("subject")?.toString() ?? "",
    message: data.get("message")?.toString() ?? "",
  };
}

function validateForm(values) {
  const errors = {};

  if (!values.name.trim()) {
    errors.name = "Name is required.";
  }

  if (!values.email.trim()) {
    errors.email = "Email is required.";
  } else if (!EMAIL_REGEX.test(values.email.trim())) {
    errors.email = "Enter a valid email address.";
  }

  if (!values.subject.trim()) {
    errors.subject = "Subject is required.";
  }

  if (!values.message.trim()) {
    errors.message = "Message is required.";
  }

  return errors;
}

function Contact() {
  const form = useRef();
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({ type: "", message: "" });
  const [errors, setErrors] = useState({});

  const sendEmail = (e) => {
    e.preventDefault();
    setStatus({ type: "", message: "" });

    const values = getFormValues(form.current);
    const validationErrors = validateForm(values);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setStatus({
        type: "error",
        message: "Please fix the errors below before sending.",
      });
      return;
    }

    setErrors({});
    setIsSending(true);

    if (!isEmailJsConfigured) {
      setIsSending(false);
      setStatus({
        type: "error",
        message:
          "Contact form is not configured. Please email info@RareLifeDesign.com directly.",
      });
      return;
    }

    emailjs
      .sendForm(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        form.current,
        EMAILJS_PUBLIC_KEY
      )
      .then(() => {
        setStatus({
          type: "success",
          message: "Message sent successfully. We'll be in touch soon.",
        });
        form.current.reset();
      })
      .catch(() => {
        setStatus({
          type: "error",
          message: "Message could not be sent. Please try again or email us directly.",
        });
      })
      .finally(() => {
        setIsSending(false);
      });
  };

  const fieldClass = (field) =>
    `contact-field w-full ${errors[field] ? "border-rld-white" : ""}`;

  return (
    <div
      id="contact"
      className="snap-section relative flex min-h-[90vh] flex-col items-center justify-center"
    >
      <div className="absolute inset-0">
        <div
          className="h-full w-full bg-cover bg-center grayscale contrast-110"
          style={{ backgroundImage: `url(${contactBg})` }}
        />
        <div className="absolute inset-0 bg-rld-black/75" />
      </div>

      <div className="card-full relative z-10 mx-auto w-11/12 max-w-5xl">
        <div className="ContactHeader p-2">
          <h1 className="text-4xl font-extrabold">Let's get in touch.</h1>
          <a className="text-2xl" href="mailto:info@RareLifeDesign.com">
            info@RareLifeDesign.com
          </a>

          {status.message && (
            <p
              id="message-status"
              role="status"
              aria-live="polite"
              className={`pt-4 text-sm md:text-base ${
                status.type === "success" ? "text-rld-white" : "text-rld-muted"
              }`}
            >
              {status.message}
            </p>
          )}
        </div>

        <form
          className="FormDiv flex flex-col"
          ref={form}
          onSubmit={sendEmail}
          noValidate
        >
          <div className="InfoDiv mt-4 flex flex-col gap-2 p-2">
            <div>
              <input
                className={fieldClass("name")}
                type="text"
                name="name"
                placeholder="Name"
                disabled={isSending}
                aria-invalid={Boolean(errors.name)}
                aria-describedby={errors.name ? "name-error" : undefined}
              />
              {errors.name && (
                <p id="name-error" className="contact-field-error">
                  {errors.name}
                </p>
              )}
            </div>

            <div>
              <input
                className={fieldClass("email")}
                type="email"
                name="email"
                placeholder="Email"
                disabled={isSending}
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <p id="email-error" className="contact-field-error">
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <input
                className={fieldClass("subject")}
                type="text"
                name="subject"
                placeholder="Subject"
                disabled={isSending}
                aria-invalid={Boolean(errors.subject)}
                aria-describedby={errors.subject ? "subject-error" : undefined}
              />
              {errors.subject && (
                <p id="subject-error" className="contact-field-error">
                  {errors.subject}
                </p>
              )}
            </div>
          </div>

          <div className="MessageDiv flex flex-col gap-2 px-2">
            <div>
              <textarea
                className={`contact-field contact-message w-full ${errors.message ? "border-rld-white" : ""}`}
                name="message"
                placeholder="Message..."
                disabled={isSending}
                aria-invalid={Boolean(errors.message)}
                aria-describedby={errors.message ? "message-error" : undefined}
              />
              {errors.message && (
                <p id="message-error" className="contact-field-error">
                  {errors.message}
                </p>
              )}
            </div>

            <input
              className="contact-submit mt-2 w-full cursor-pointer border border-rld-white bg-rld-white px-4 py-2 font-semibold text-rld-black transition-colors duration-200 hover:bg-transparent hover:text-rld-white disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-rld-white disabled:hover:text-rld-black"
              type="submit"
              value={isSending ? "Sending..." : "Submit"}
              disabled={isSending}
            />
          </div>
        </form>
      </div>
    </div>
  );
}

export default Contact;
